import { useEffect, useState } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { useAtomValue } from 'jotai'
import {
  Container, Stack, Title, Text, Paper, TextInput, Button, Group,
  Loader, Center, Alert, Box,
} from '@mantine/core'
import { IconAlertCircle, IconUsers, IconArrowLeft } from '@tabler/icons-react'
import { authUserAtom, isAuthenticatedAtom, isProvisionalAtom, authHydratedAtom } from '../atoms/authAtoms'
import { useTeamByCode, useJoinTeam } from '../hooks/useTeam'

function deriveDefaultDisplayName(email) {
  if (!email) return ''
  const handle = email.split('@')[0]
  return handle.charAt(0).toUpperCase() + handle.slice(1)
}

export default function TeamJoin() {
  const { code } = useParams()
  const navigate = useNavigate()
  const hydrated = useAtomValue(authHydratedAtom)
  const isAuthenticated = useAtomValue(isAuthenticatedAtom)
  const isProvisional = useAtomValue(isProvisionalAtom)
  const user = useAtomValue(authUserAtom)

  // Provisional accounts can't join a team — promote to login/signup.
  // Unauthenticated visitors get bounced to login with a returnTo so they
  // land back here after authenticating.
  useEffect(() => {
    if (!hydrated) return
    if (!isAuthenticated || isProvisional) {
      sessionStorage.setItem('bw_returnTo', `/team/${code}`)
      navigate(isProvisional ? '/signup' : '/login')
    }
  }, [hydrated, isAuthenticated, isProvisional, code, navigate])

  const enabled = hydrated && isAuthenticated && !isProvisional
  const { data: lookup, isLoading, error } = useTeamByCode(enabled ? code : null)

  // Derive the default display name during render rather than syncing into
  // state via an effect — avoids cascading renders when lookup/user resolve.
  // Switch to "user-edited" mode the first time the field changes.
  const [edited, setEdited] = useState(false)
  const [displayNameDraft, setDisplayNameDraft] = useState('')
  const computedDefault = lookup?.suggested_display_name
    || (user?.email ? deriveDefaultDisplayName(user.email) : '')
  const displayName = edited ? displayNameDraft : computedDefault
  const updateDisplayName = (val) => {
    if (!edited) setEdited(true)
    setDisplayNameDraft(val)
  }

  const join = useJoinTeam(lookup?.team_id)

  // Already a member — just route to the dashboard.
  useEffect(() => {
    if (lookup?.already_member && lookup?.team_id) {
      navigate(`/teams/${lookup.team_id}`, { replace: true })
    }
  }, [lookup, navigate])

  const handleJoin = async (e) => {
    e.preventDefault()
    if (!displayName.trim() || !lookup?.team_id) return
    await join.mutateAsync(displayName.trim())
    navigate(`/teams/${lookup.team_id}`)
  }

  const inputStyles = {
    input: {
      background: 'rgba(10, 22, 40, 0.8)',
      border: '1px solid rgba(0, 255, 255, 0.15)',
      color: '#e0e8f0',
    },
    label: { color: 'var(--mantine-color-gray-4)' },
  }

  if (!hydrated || !enabled) {
    return (
      <Container size="sm" py="xl">
        <Center py="xl"><Loader color="cyan" /></Center>
      </Container>
    )
  }

  return (
    <Container size="sm" py="xl">
      <Stack gap="lg">
        <Title order={2} ff="Orbitron, sans-serif" c="cyan.4">
          Join Team
        </Title>

        {isLoading && (
          <Center py="xl"><Loader color="cyan" /></Center>
        )}

        {error && (
          <Paper p="lg" className="glass">
            <Stack gap="md">
              <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
                {error.response?.status === 404
                  ? 'This invite link is invalid or has expired.'
                  : (error.response?.data?.error || 'Could not load team. Try again.')}
              </Alert>
              <Group>
                <Button
                  variant="light"
                  color="gray"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={() => navigate('/teams')}
                >
                  My Teams
                </Button>
              </Group>
            </Stack>
          </Paper>
        )}

        {lookup && !lookup.eligible_to_join && (
          <Paper p="lg" className="glass">
            <Stack gap="md">
              <Alert icon={<IconAlertCircle size={16} />} color="yellow" variant="light" title="Access required">
                <Text size="sm">
                  You need access to{' '}
                  <Text span fw={600} c="cyan.3">
                    {lookup.current_story_name || 'this story'}
                  </Text>
                  {' '}to join this team. Contact your facilitator{lookup.missing_group_name ? ` or enroll in ${lookup.missing_group_name}` : ''} and try the link again.
                </Text>
              </Alert>
              <Group>
                <Button
                  variant="light"
                  color="gray"
                  leftSection={<IconArrowLeft size={16} />}
                  onClick={() => navigate('/')}
                >
                  Browse Stories
                </Button>
              </Group>
            </Stack>
          </Paper>
        )}

        {lookup && lookup.eligible_to_join && !lookup.already_member && (
          <Paper p="xl" className="glass">
            <form onSubmit={handleJoin}>
              <Stack gap="md">
                <Box>
                  <Text size="xs" c="dimmed" ff="Orbitron, sans-serif" tt="uppercase" lts={1}>
                    Joining
                  </Text>
                  <Text size="lg" fw={600} c="cyan.3" ff="Orbitron, sans-serif">
                    {lookup.team_name}
                  </Text>
                  {lookup.current_story_name && (
                    <Text size="sm" c="dimmed">
                      Currently playing: {lookup.current_story_name}
                    </Text>
                  )}
                </Box>

                <TextInput
                  label="Display name"
                  description="How you'll appear to other team members in comparison views."
                  placeholder="Your team display name"
                  value={displayName}
                  onChange={(e) => updateDisplayName(e.currentTarget.value)}
                  required
                  styles={inputStyles}
                />

                <Group justify="flex-end" mt="md">
                  <Button
                    type="submit"
                    leftSection={<IconUsers size={16} />}
                    loading={join.isPending}
                    disabled={!displayName.trim()}
                  >
                    Join Team
                  </Button>
                </Group>
              </Stack>
            </form>
          </Paper>
        )}
      </Stack>
    </Container>
  )
}
